import { animate, group, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSortHeader, Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, mergeMap, take, takeUntil } from 'rxjs/operators';

import { InstallationItem, RemoteConfigBanners, StatusType } from '@vi-iot/client';

import { DataService, GTMService, RoutingService, UIService } from '@services';
import {
  AuthStore,
  BackendStorageStore,
  BookingStore,
  InstallationsFilterType,
  InstallationsStore,
  RemoteConfigStore,
  StatusPageStore,
  VkOrgCountry,
  defaultPageSize,
  storageSessionsKeys,
} from '@store';

import { LiveUpdatesDataService } from '../../shared/live-updates/live-updates-data.service';
import { InstallationsService, Param, Sorter } from '../services/installations.service';

import { SocketIoService } from 'src/app/shared/socket-io/socket-io.service';
import { MAINTAINER_DASHBOARD_WIDTH, RSA_DASHBOARD_WIDTH } from '../../shared/common/ui-constants';
import { AnnouncementBannerComponent } from './announcement-banner/announcement-banner.component';
import { InstallationSearchComponent } from './installation-search/installation-search.component';

@Component({
  selector: 'app-installations',
  templateUrl: './installations.component.html',
  styleUrls: ['./installations.component.scss'],
  animations: [
    trigger('fadeOut', [
      transition('* => void', [
        group([
          animate(
            '1s ease',
            style({
              opacity: 0.3,
              pointerEvents: 'none',
            })
          ),
          animate(
            '0.3s 1s ease',
            style({
              height: 0,
            })
          ),
        ]),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationsComponent implements OnInit, OnDestroy {
  statusPageType = StatusType;
  installations$: Observable<InstallationItem[]>;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  sorters: Sorter[];
  param: Param;
  defaultPageSize: number;
  activeSearch: boolean;
  overviewUrl = 'https://systems-overview.viessmann-climatesolutions.com/list';
  isRsaUser: boolean;
  isTableDesktopView: boolean;
  vitoconnectIssueBanner = RemoteConfigBanners.vitoconnectIssueBanner;
  vitoconnectLink: string;
  executeOnlyOnce: boolean;
  isUserFromGermanyOrAustria: boolean;
  advertisingBannerForDeAndAu = RemoteConfigBanners.advertisingBannerForDeAndAu;

  readonly advertBannerLink =
    'https://live.viessmann-climatesolutions.com/de/akademie-service.html';
  contentWidth = RSA_DASHBOARD_WIDTH + 'px';

  @ViewChild(InstallationSearchComponent, { static: true })
  searchComp: InstallationSearchComponent;

  @ViewChildren(MatSortHeader)
  sortHeaders: QueryList<MatSortHeader>;

  @HostListener('window:resize', ['$event'])
  calculateResponsiveness(): void {
    this.remoteConfigStore.hasAccessToWholeInstallationsTable$
      .pipe(first(), takeUntil(this.ngUnsubscribe))
      .subscribe((hasAccess) => {
        if (hasAccess) {
          this.contentWidth = MAINTAINER_DASHBOARD_WIDTH + 'px';
          this.isTableDesktopView = window.innerWidth > MAINTAINER_DASHBOARD_WIDTH;
        } else {
          this.contentWidth = RSA_DASHBOARD_WIDTH + 'px';
          this.isTableDesktopView = window.innerWidth > RSA_DASHBOARD_WIDTH;
        }
      });
  }

  constructor(
    public installationsService: InstallationsService,
    public installationsStore: InstallationsStore,
    public dataService: DataService,
    public authStore: AuthStore,
    public remoteConfigStore: RemoteConfigStore,
    public statusPageStore: StatusPageStore,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private uiService: UIService,
    private gtmService: GTMService,
    private liveUpdatesDataService: LiveUpdatesDataService,
    private routingService: RoutingService,
    private bookingStore: BookingStore,
    private dialog: MatDialog,
    public backendStorageStore: BackendStorageStore,
    private socketService: SocketIoService
  ) {
    window.scrollTo(0, 0);
  }

  ngOnInit(): void {
    this.liveUpdatesDataService.unsubscribeAll();
    this.setIsRsaUser();
    this.setInitValues();

    this.activatedRoute.queryParams.subscribe((qp) => {
      if (qp?.unfollowedId) {
        this.installationsService.removeFromSource(+qp?.unfollowedId);
        this.router.navigate(['/installations']);
      }
    });

    this.activatedRoute.queryParams
      .pipe(
        takeUntil(this.ngUnsubscribe),
        mergeMap((params) => combineLatest([of(params), this.installationsStore.isData$])),
        filter(([_qp, isData]) => isData)
      )
      .subscribe(([qp]) => {
        this.param.pageIndex = this.installationsService.coercePageParam(qp['page']);
        this.param.pageSize = this.installationsService.coerceSizeParam(qp['size']);
        this.param.sortBy = this.installationsService.coerceSortParam(qp['sortBy']);
        this.param.orderBy = this.installationsService.coerceOrderParam(qp['orderBy']);
        this.param.filter = this.installationsService.coerceFilterParam(qp['filter']);
        this.param.search = this.installationsService.coerceSearchParam(qp['search']);
        this.processFilters();
        if (!this.executeOnlyOnce) {
          this.setPageIndexSavedValue();
        }
        this.executeOnlyOnce = true;
      });

    this.installations$ = this.installationsService.installations$;

    this.installationsService.isSearchInfoBox$.subscribe((res) => {
      if (res) {
        this.param.search = undefined;
      }
    });

    this.authStore.showLearningRoomBanner();
    this.authStore.showEventBanner();
    this.authStore.showPaidFeaturesBanner();

    this.authStore
      .getVkorg()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((vkorg: string) => !!vkorg)
      )
      .subscribe((vkorg: string) => {
        // Domain change - deletion recommended
        if (vkorg.includes(VkOrgCountry.Germany)) {
          this.vitoconnectLink =
            'https://partnerportal.viessmann-climatesolutions.com/de/de/informieren/vitoconnect--problem-bei-der-konnektivierung.html';
        } else if (vkorg.includes(VkOrgCountry.Austria)) {
          this.vitoconnectLink =
            'https://partnerportal.viessmann-climatesolutions.com/at/de/informieren/vitoconnect--problem-bei-der-konnektivierung.html';
        }
        this.isUserFromGermanyOrAustria =
          vkorg.includes(VkOrgCountry.Germany) || vkorg.includes(VkOrgCountry.Austria);
      });

    this.backendStorageStore.getKeyValue(storageSessionsKeys.ADVERT_BANNER_KEY);
    this.fetchSubscriptionsAndInstallations();
    this.calculateResponsiveness();
    this.showAnnouncementBanner();
    this.installationsStore.fetchNumberOfDisplayedInstallations();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.installationsService.resetSubscriptions();
    sessionStorage.removeItem(this.socketService.liveUpdatesSubscriptionSessionKey);
  }

  setInitValues(): void {
    this.defaultPageSize = defaultPageSize;
    this.sorters = this.installationsService.sorters;
    this.param = {
      pageIndex: 0,
      pageSize: defaultPageSize,
      sortBy: undefined,
      orderBy: '',
      filter: undefined,
      search: undefined,
    };
  }

  processFilters(): void {
    if (this.param.sortBy && this.param.pageIndex === 0) {
      this.installationsService.sortInstallations(this.param);
    } else if (!this.param.sortBy && this.param.pageIndex === 0) {
      this.installationsService.pageInstallations(
        this.param.pageIndex,
        this.param.pageSize,
        true,
        this.param.filter,
        this.param.search
      );
    } else {
      this.installationsService.pageInstallations(
        this.param.pageIndex,
        this.param.pageSize,
        false,
        this.param.filter,
        this.param.search
      );
    }
    this.refreshSortHeaders();
  }

  refreshSortHeaders(): void {
    if (!this.sortHeaders) {
      return;
    }

    setTimeout(() => {
      this.sortHeaders.forEach((sortHeader) => {
        sortHeader._sort.active = this.param.sortBy;
        sortHeader._sort.direction = this.param.orderBy;
        sortHeader._setAnimationTransitionState({
          toState: sortHeader._isSorted() ? 'active' : sortHeader._arrowDirection,
        });
      });
    });
  }

  page(paginator: PageEvent | Param): void {
    if (this.param.pageSize !== paginator.pageSize) {
      this.gtmService.track('onSize');
      this.installationsStore.setNumberOfDisplayedInstallations(paginator.pageSize);
    }
    if (this.param.pageIndex !== paginator.pageIndex) {
      this.gtmService.track('onPage');
    }

    this.uiService.installationsQueryParams = {
      page: paginator.pageIndex ? paginator.pageIndex + 1 : undefined,
      size: paginator.pageSize !== this.defaultPageSize ? paginator.pageSize : undefined,
      sortBy: this.param.sortBy,
      orderBy: this.param.orderBy || undefined,
      filter: this.param.filter,
      search: this.param.search,
    };
    this.routingService.updateUrl(this.uiService.installationsQueryParams);
  }

  sort(sort: Sort): void {
    this.uiService.installationsQueryParams = {
      size: this.param.pageSize !== this.defaultPageSize ? this.param.pageSize : undefined,
      sortBy: sort.direction ? sort.active : undefined,
      orderBy: sort.direction || undefined,
      filter: this.param.filter,
      search: this.param.search,
    };
    this.routingService.updateUrl(this.uiService.installationsQueryParams);
    this.gtmService.track('onSort');
  }

  filter(name: InstallationsFilterType): void {
    this.uiService.installationsQueryParams = {
      size: this.param.pageSize !== this.defaultPageSize ? this.param.pageSize : undefined,
      sortBy: undefined,
      orderBy: undefined,
      filter: this.installationsService.coerceFilterParam(name),
      search: this.param.search,
    };
    this.routingService.updateUrl(this.uiService.installationsQueryParams);
  }

  search(text: string): void {
    this.uiService.installationsQueryParams = {
      size: this.param.pageSize !== this.defaultPageSize ? this.param.pageSize : undefined,
      sortBy: undefined,
      orderBy: undefined,
      filter: this.param.filter,
      search: this.installationsService.coerceSearchParam(text),
    };
    this.routingService.updateUrl(this.uiService.installationsQueryParams);
    this.gtmService.track('onSearch', 'Installations', 'overview');
  }

  clearSearch(): void {
    this.activeSearch = false;
    this.gtmService.track('onClearSearch');
  }

  areProcessedInstallations(): boolean {
    return this.installationsService.processedInstallations.length > 0;
  }

  isPaginator(): boolean {
    return (
      this.areProcessedInstallations() &&
      this.installationsService.processedInstallations.length >= 6
    );
  }

  installationTrackBy(_index: number, installation: any): any {
    return installation.id + !!installation.heatingModes;
  }

  private setIsRsaUser(): void {
    this.authStore
      .isRsaUser()
      .pipe(first(), takeUntil(this.ngUnsubscribe))
      .subscribe((isRsa) => (this.isRsaUser = isRsa));
  }

  private fetchSubscriptionsAndInstallations(): void {
    this.authStore.getCompanyIdForInstallationsListInit$
      .pipe(first(), takeUntil(this.ngUnsubscribe))
      .subscribe((model) => {
        if (model.installationsCount === 0) {
          this.installationsStore.listInstallations();
        }
        if (!model.isRsaUser && model.companyId) {
          this.bookingStore.getAllVitoguideSubscriptions(model.companyId);
        }
      });
  }

  private showAnnouncementBanner(): void {
    this.remoteConfigStore
      .showInstallationsAnnouncementBanner()
      .pipe(
        filter((res) => Boolean(res)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.dialog.open(AnnouncementBannerComponent, {
          panelClass: 'installations-announcement-banner',
          width: '800px',
          minHeight: '300px',
          height: 'auto',
          disableClose: true,
          autoFocus: false,
          backdropClass: 'black-backdrop',
        });
      });
  }

  private setPageIndexSavedValue(): void {
    this.installationsStore.numberOfDisplayedInstallations$
      .pipe(
        filter((val) => !!val),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((value) => {
        this.page({ ...this.param, pageSize: value });
      });
  }

  onAdvertBannerClose(): void {
    this.backendStorageStore.setKeyValue(storageSessionsKeys.ADVERT_BANNER_KEY, { data: false });
  }
}
